import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, LogIn, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setToken, isAuthenticated } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      setToken(res.data.access_token);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-6" data-testid="login-page">
      <div className="absolute top-6 left-6">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-[#7dd3fc] transition-colors" data-testid="login-back-link">
          <ArrowLeft size={18} />
          <span>Voltar ao site</span>
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_flavored-ice-factory/artifacts/iyqyn1tc_IMG_3682.png"
            alt="Império Ice"
            className="h-24 w-auto object-contain mx-auto mb-6 drop-shadow-[0_0_25px_rgba(255,255,255,0.5)]"
          />
          <h1 className="text-3xl font-bold text-white">
            Painel <span className="text-[#7dd3fc]">Admin</span>
          </h1>
          <p className="text-white/50 mt-2">Faça login para continuar</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 space-y-5">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm text-center" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-white/70 text-sm mb-2">E-mail</label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@imperioice.com.br"
                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/70 text-sm mb-2">Senha</label>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#7dd3fc]/50 transition-colors"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            className="w-full py-3.5 bg-gradient-to-r from-[#7dd3fc] to-[#bae6fd] text-[#0a1628] font-bold rounded-xl shadow-lg shadow-[#7dd3fc]/30 hover:shadow-xl hover:shadow-[#7dd3fc]/40 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
            data-testid="login-submit-btn"
          >
            <LogIn size={18} />
            <span>{loading ? 'Entrando...' : 'Entrar'}</span>
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}
